import {
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, merge, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { TUISettings } from 'src/app/project/modules/ui/ui.model';
import { TUser } from 'src/app/project/modules/user/user.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { TSync } from '../../sync/sync.model';
import { TPoint, TPointsByWorkspace } from '../points.model';

import { UnsavedFilesModalComponent } from '../unsaved-files-modal/unsaved-files-modal.component';

import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PlanPointService } from 'src/app/project/modules/plan/plan-point/plan-point.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { SiteTablePointsService } from '../../site/site-table/site-table-points.service';
import {
  PointAttachmentsService,
  TFilesToUpload,
} from '../point-modal/point-attachments/point-attachments.service';
import { PointsDetailsService } from '../points-details.service';
import { PointsFetchingService } from '../points-fetching.service';
import { PointsService } from '../points.service';
import { PointHalfModalService, TPointHalfModal } from './point-half-modal.service';

import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { EKeyboardKeyCode } from 'src/app/core/helpers/keyboard-codes';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { GET_ACTIVE_PLAN } from 'src/app/project/modules/plan/plan.store';
import {
  canDeletePoint,
  canEditPoint,
} from 'src/app/project/modules/share/share-utils/share-permissions';
import Table from 'src/app/project/modules/site/site-table/custom-table/table/Table';
import Timeline from 'src/app/project/modules/site/site-timeline/Timeline';
import { GET_TIMELINE } from 'src/app/project/modules/site/site-timeline/timeline.ui.store';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { TPointDetails } from 'src/app/project/view-models/points-details-response.model';
import { SiteRedirectService } from '../../auth/site-redirect.service';
import { RemindersService } from '../../reminders/reminders.service';
import { CustomTableService } from '../../site/site-table/custom-table/custom-table.service';
import { SiteTableService } from '../../site/site-table/site-table.service';
import { PointsGenerateService } from '../point-generate/points-generate.service';
import { PointModalHeaderService } from '../point-modal-header/point-modal-header.service';
import { PointArrowRedirectService } from '../point-modal/point-arrow-redirect.service';
import { selectPointFromState } from '../utils/select-point-from-state';

@Component({
  selector: 'pp-point-half-modal',
  templateUrl: './point-half-modal.component.html',
  styleUrls: ['./point-half-modal.component.scss'],
  standalone: false,
})
export class PointHalfModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly paramsChanged$ = new Subject<void>();
  modal: TPointHalfModal = this.pointHalfModalService.getModal();
  private filesToUpload: TFilesToUpload = this.pointAttachmentsService.getFilesToUpload();

  workspaces$: Observable<TWorkspacesById>;
  private sync$: Observable<TSync>;
  private points$: Observable<TPointsByWorkspace>;
  user$: Observable<TUser>;
  user: TUser;
  ui$: Observable<TUISettings>;
  ui: TUISettings;
  canEditPoint = false;
  canDeletePoint = false;

  point: TPoint;
  workspaceId: string;
  hasWorkspaces = false;
  scrollPosition = 0;
  activePlan = GET_ACTIVE_PLAN();
  pointDetails: TPointDetails = null;
  isTimeline = false;
  isOverview = false;
  nextPointExists = false;
  detailsFetched = false;
  freshPointFetched = false;

  _id: string;
  commentsEditable = false;
  attachmentUploading = false;
  planWidth: number;

  workspace: TWorkspace;
  private workspaces: TWorkspacesById;
  relatedPointsFlagEnabled = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private store: Store<{
      workspaces: TWorkspacesById;
      sync: TSync;
      points: TPointsByWorkspace;
      user: TUser;
      ui: TUISettings;
    }>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private pointHalfModalService: PointHalfModalService,
    private pointAttachmentsService: PointAttachmentsService,
    private pointsService: PointsService,
    private siteTablePointsService: SiteTablePointsService,
    private permissionsService: PermissionsService,
    private promptService: PromptService,
    private pointsFetchingService: PointsFetchingService,
    private planPointService: PlanPointService,
    private translationPipe: TranslationPipe,
    private pointsDetailsService: PointsDetailsService,
    private pointModalHeaderService: PointModalHeaderService,
    private sitePointFilterService: SitePointFilterService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private customTableService: CustomTableService,
    private pointsGenerateService: PointsGenerateService,
    private siteTableService: SiteTableService,
    private siteRedirectService: SiteRedirectService,
    private remindersService: RemindersService,
    private pointArrowRedirectService: PointArrowRedirectService,
    private featureFlagService: FeatureFlagService,
    private ngZone: NgZone,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
    this.sync$ = this.store.pipe(select(EStore.SYNC));
    this.points$ = this.store.pipe(select(EStore.POINTS));
    this.user$ = this.store.pipe(select(EStore.USER));
    this.ui$ = this.store.pipe(select(EStore.UI));
  }

  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent): void {
    this.pointArrowRedirectService.tryRedirectPointOnKeyup({
      event,
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: false,
    });
  }

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent): void {
    if (
      event.key !== EKeyboardKeyCode.ArrowDown &&
      event.key !== EKeyboardKeyCode.ArrowUp &&
      event.key !== EKeyboardKeyCode.ArrowLeft &&
      event.key !== EKeyboardKeyCode.ArrowRight
    ) {
      return;
    }

    this.pointArrowRedirectService.preventArrowEventIfNeeded(event);
  }
  ngOnInit() {
    this.relatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => this.userHandler(user));
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.paramsHandler(params));
    this.workspaces$
      .pipe(takeUntil(this.destroy$))
      .subscribe((workspaces) => this.workspacesHandler(workspaces));
    this.points$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) =>
            selectPointFromState(prev, this.workspaceId, this._id) ===
            selectPointFromState(curr, this.workspaceId, this._id),
        ),
      )
      .subscribe((points) => this.pointsHandler(points));
    this.pointAttachmentsService.fileUploading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUploading) => this.fileUploadingHandler(isUploading));
    this.ui$.pipe(takeUntil(this.destroy$)).subscribe((ui) => this.uiHandler(ui));

    this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.point && this.point.sequenceNumber) {
        this.siteTablePointsService.dehighlightPoint();
      }
    });

    this.featureFlagService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.relatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);
    });
  }

  ngOnDestroy() {
    if (this.modal.visible) {
      this.hide(false);
    }

    if (this.point) {
      this.siteTablePointsService.dehighlightPoint();
    }

    this.paramsChanged$.next();
    this.destroy$.next();
    this.updateSitePlan();
  }

  userHandler(user: TUser): void {
    this.user = user;
  }

  paramsHandler(params: ParamMap): void {
    this.paramsChanged$.next();

    const openTeardown$ = merge(this.destroy$, this.paramsChanged$);

    this.isTimeline = this.router.url.search(EWorkspaces.TIMELINE) !== -1;
    this.isOverview = this.router.url.search(EWorkspaces.OVERVIEW) !== -1;

    if (this.point && this.point.sequenceNumber) {
      this.siteTablePointsService.dehighlightPoint();
    }

    this._id = params['params']._id;
    this.generatePoint(this.pointsService.findPoint(this._id));
    this.pointDetails = null;

    this.prepareWorkspace()
      .pipe(
        takeUntil(openTeardown$),
        tap(() => {
          if (this.isOverview || this.isTimeline) {
            this.fetchPoint();
          } else if (this.siteTableService.getTableLoaded()) {
            this.fetchPoint();
          } else {
            this.siteTableService.tableLoadChange$
              .pipe(
                filter((tableLoaded) => tableLoaded),
                take(1),
                takeUntil(openTeardown$),
                tap(() => this.fetchPoint()),
              )
              .subscribe();
          }
        }),
      )
      .subscribe();

    if (this.point) {
      this.openPointModal();
    } else {
      this.fetchPointThenOpen(openTeardown$);
    }

    this.planWidth = this.ui?.sitePlanWidth?.[this.workspaceId];
  }

  private fetchPointThenOpen(openTeardown$: Observable<void>): void {
    this.sync$
      .pipe(
        filter((sync) => sync.basicFetched),
        take(1),
        switchMap(() => this.pointsFetchingService.fetchPoint(this._id)),
        takeUntil(openTeardown$),
        tap((point) => this.openFetchedPoint(point)),
        catchError(() => {
          this.handlePointFetchError();

          return of();
        }),
      )
      .subscribe();
  }

  private openFetchedPoint(point: TPoint): void {
    if (!point) {
      this.onPointLoadError();

      return;
    }

    this.generatePoint(this.pointsService.findPoint(this._id) ?? point);
    this.openPointModal();
  }

  private openPointModal(): void {
    this.workspaceId = this.point.workspaceRef.id;

    this.workspacesHandler(this.workspaces);
    this.activateTablePoint();
    this.showModal();
  }

  private onPointLoadError(): void {
    const prompt = this.translationPipe.transform('prompt_point_moved_deleted');

    this.promptService.showWarning(prompt);
    this.pointHalfModalService.hideModal({ returnToSite: true, error: true });
  }

  private handlePointFetchError(): void {
    const workspaceId = this.activeService.getActiveWorkspaceId();
    const hasSiteAccess = !!this.workspaceService.getWorkspaces()[workspaceId];

    if (hasSiteAccess) {
      this.onPointLoadError();
    } else {
      this.siteRedirectService.redirectToSites();
    }
  }

  workspacesHandler(workspaces: TWorkspacesById): void {
    this.workspaces = workspaces;
    this.hasWorkspaces = !!workspaces;

    if (this.hasWorkspaces) {
      this.workspace = this.workspaces[this.workspaceId];
    }

    this.checkCommentsPermissions();
  }

  pointsHandler(points: TPointsByWorkspace): void {
    if (Object.keys(points).length === 0) {
      return;
    }

    const point = this.pointsService.findPoint(this._id);

    if (!(point && this.workspace)) {
      this.fetchPoint();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.generatePoint(structuredClone(point));

      if (this.isTimeline) {
        const timeline: Timeline = GET_TIMELINE();
        this.nextPointExists = timeline?.getPoints().length > 1;
      } else {
        const table: Table = this.customTableService.getTable();
        this.nextPointExists = table?.getPoints().length > 1;
      }
    });

    if (this.workspace.customFields.length !== Object.keys(point.customFieldsMap).length) {
      this.fetchPoint();
    }
  }

  fetchPoint(): void {
    this.detailsFetched = false;
    this.freshPointFetched = false;

    this.pointsFetchingService
      .fetchPoint(this._id)
      .pipe(
        takeUntil(this.destroy$),
        tap((point) => {
          this.freshPointFetched = true;
          this.siteTablePointsService.updateTablePoint(point);
          this.updateSitePlan();
        }),
        catchError((error) => {
          if (error.status === EStatusCode.FORBIDDEN) {
            this.handlePointFetchError();
          }

          return of();
        }),
      )
      .subscribe();

    this.pointsDetailsService
      .fetchPointDetails(this._id)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.pointDetails = this.pointsDetailsService.getPointDetails(this._id || null);
          this.detailsFetched = true;
        }),
      )
      .subscribe();
  }

  fileUploadingHandler(isUploading: boolean): void {
    this.attachmentUploading = isUploading;
  }

  uiHandler(ui: TUISettings): void {
    this.ui = ui;
    this.planWidth = this.ui?.sitePlanWidth?.[this.workspaceId];
  }

  checkCommentsPermissions(): void {
    if (this.workspace && this.workspaceId) {
      this.canEditPoint = canEditPoint(this.workspace.share.shareOption);
      this.canDeletePoint = canDeletePoint(this.workspace.share.shareOption);

      const commentPermissions = this.permissionsService.getCommentPermissions(
        this.workspace.workspaceId,
      );

      this.commentsEditable = commentPermissions.edit;
    }
  }

  updateScrollPosition(event: number): void {
    this.scrollPosition = event;
  }

  updateSitePlan(): void {
    this.planPointService.updateFeature(this._id, this.workspaceId, true);
    this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
  }

  prevPoint(): void {
    this.pointModalHeaderService.prevPoint({
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      _id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: false,
    });
  }

  nextPoint(): void {
    this.pointModalHeaderService.nextPoint({
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      _id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: false,
    });
  }

  expandModal(): void {
    if (!this.attachmentUploading && !this.activePlan.active) {
      this.pointModalHeaderService.expandModal({
        attachmentUploading: this.attachmentUploading,
        _id: this._id,
        workspaceId: this.workspaceId,
        isTimeline: this.isTimeline,
        isOverview: this.isOverview,
      });
    }
  }

  hide(returnToSite: boolean = true): void {
    if (!this.attachmentUploading && !this.activePlan.active) {
      if (this.filesToUpload.data.length > 0) {
        for (let index = 0; index < this.filesToUpload.data.length; index += 1) {
          const file = this.filesToUpload.data[index];

          if (!file.uploaded) {
            this.modalService.showModal(UnsavedFilesModalComponent);
            break;
          } else {
            this.pointHalfModalService.hideModal({ returnToSite });
          }
        }
      } else {
        this.pointHalfModalService.hideModal({ returnToSite });
      }
    }
  }

  private showModal(): void {
    this.pointHalfModalService.showModal(
      this.workspaceId,
      this._id,
      this.isOverview,
      this.isTimeline,
    );
    this.siteTablePointsService.highlightPoint(
      this.point.sequenceNumber ? this.point.sequenceNumber.toString() : '',
    );
  }

  private prepareWorkspace(): Observable<TWorkspace> {
    if (this.point && this.workspaces?.[this.point.workspaceRef.id]) {
      return of(null);
    }

    const workspaceId = this.point
      ? this.point.workspaceRef.id
      : this.activeService.getActiveWorkspaceId();

    return this.workspaceService.generateWorkspace(workspaceId);
  }

  private activateTablePoint(): void {
    if (this.isTimeline) {
      return;
    }

    const table: Table = this.customTableService.getTable();

    if (table) {
      const point = table.getPoints().find((_point) => _point._id === this._id);

      if (table.tableBody && point) {
        table.activatePoint(point.sequenceNumber);
      }
    }
  }

  generatePoint(point: TPoint): void {
    this.point = this.pointsGenerateService.correctPoint(point);
  }
}
